import * as selectors from './selectors';
import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import {
  fetchAgenda,
  fetchAgendaError,
  fetchAgendaSuccess,
  updateAgenda,
  updateAgendaError,
  updateAgendaSucess
} from './actions';
import { getAgenda, saveAgenda } from '../../utils/api/casesApi';
import { DetailsAgendaActionTypes, DetailsAgendaState } from './types';
import { DateTime } from 'luxon';
import { getI18n } from '../../utils/i18n/strings.manager';
import { toggleIsUIDisabled } from '../../shared/actions';
import { SagaIterator } from 'redux-saga';
import { AgendaResponse } from './model';

function* handleFetchAgenda(action: ReturnType<typeof fetchAgenda>): SagaIterator {
  try {
    const res = yield call(getAgenda, action.payload.instructionId);

    if (!res.data) {
      yield put(fetchAgendaError(JSON.stringify(res.response)));
    } else {
      yield put(fetchAgendaSuccess(res.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchAgendaError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchAgendaError('An unknown error occurred.'));
    }
  }
}

function convertDetailsAgendaStateToAgendaResponse(details: DetailsAgendaState): AgendaResponse {
  const response = new AgendaResponse();
  response.selectedHistoryId = details.selectedHistoryId ?? 0;
  response.plannedOnDate = details.plannedOnDate;
  response.agendaReference = details.agendaReference;
  response.status = details.status;
  response.agendaHistoryAuditList = details.agendaHistoryAuditList;
  return response;
}

function* handleUpdateAgenda(action: ReturnType<typeof updateAgenda>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const instructionId = yield select(selectors.instructionId);
    const data: DetailsAgendaState = yield select(selectors.agendaState);

    // Convert plannedOnDate to a Luxon DateTime object
    const plannedOnDate = typeof data.plannedOnDate === 'string' 
      ? DateTime.fromISO(data.plannedOnDate) // Convert ISO string to DateTime
      : DateTime.fromJSDate(data.plannedOnDate); // Convert Date object to DateTime

    if (!plannedOnDate.isValid) {
      throw new Error("plannedOnDate is invalid. Check the value being passed.");
    }

    // Set plannedOnTime (hour and minute) in the DateTime object
    const luxonDate = plannedOnDate.set({
      hour: getI18n().getHours(data.plannedOnTime),
      minute: getI18n().getMinutes(data.plannedOnTime),
    });

    // Convert Luxon DateTime back to JavaScript Date for API compatibility
    const convertedData: AgendaResponse = convertDetailsAgendaStateToAgendaResponse(data);
    convertedData.plannedOnDate = luxonDate.toJSDate();

    const res = yield call(saveAgenda, instructionId, convertedData);
    if (res.status !== 200) {
      yield put(updateAgendaError(JSON.stringify(res.response)));
    } else {
      yield put(updateAgendaSucess(res.data));
      yield put(fetchAgenda(instructionId));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(updateAgendaError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(updateAgendaError('An unknown error occurred.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}


function* watchAgendaDetails() {
  yield takeEvery(DetailsAgendaActionTypes.FETCH_AGENDA, handleFetchAgenda);
  yield takeEvery(DetailsAgendaActionTypes.UPDATE_AGENDA, handleUpdateAgenda);
}

function* detailsAgendaSaga() {
  yield all([fork(watchAgendaDetails)]);
}

export default detailsAgendaSaga;
